import React, {
  cloneElement,
  memo,
  ReactElement,
  ReactNode,
  useState,
} from 'react';
import { Popover } from 'antd';
import { TooltipPlacement } from 'antd/lib/tooltip';

type HoverCardTrigger = 'hover' | 'focus' | 'click';

export interface IHoverCardProps {
  /**
   * Содержимое. Функция получает `armed` — карточка хоть раз открывалась:
   * запрос внутри откладывается до первого открытия (проход мышью по
   * таблице не шлёт ни одного запроса).
   */
  content: ReactNode | ((armed: boolean) => ReactNode);
  title?: ReactNode;
  placement?: TooltipPlacement;
  /** По умолчанию — наведение и фокус (клавиатура тоже открывает). */
  trigger?: HoverCardTrigger | HoverCardTrigger[];
  /** Задержка открытия, с; наведение «мимоходом» карточку не открывает. */
  delay?: number;
  children: ReactElement;
}

/**
 * ТЗ 0103 — карточка при наведении (паттерн `StudentHoverCard`): antd
 * `Popover`, задержка 0,4 с, содержимое снимается при закрытии, а `armed`
 * откладывает запрос до первого открытия. Клик-вариант (`trigger="click"`)
 * — поповер «Почему» у карточки действия.
 */
export const HoverCard = memo(
  ({
    content,
    title,
    placement = 'top',
    trigger = ['hover', 'focus'],
    delay = 0.4,
    children,
  }: IHoverCardProps) => {
    const [armed, setArmed] = useState(false);
    const [open, setOpen] = useState(false);
    // Клик-вариант — раскрывашка: кнопка говорит чтецу, открыта ли карточка
    // (`aria-expanded`). Наведение — подсказка, у неё этого нет.
    const byClick = Array.isArray(trigger)
      ? trigger.includes('click')
      : trigger === 'click';
    return (
      <Popover
        title={title}
        content={typeof content === 'function' ? content(armed) : content}
        placement={placement}
        trigger={trigger}
        mouseEnterDelay={delay}
        mouseLeaveDelay={0.1}
        destroyTooltipOnHide
        onVisibleChange={(visible) => {
          if (visible) setArmed(true);
          setOpen(visible);
        }}
      >
        {byClick ? cloneElement(children, { 'aria-expanded': open }) : children}
      </Popover>
    );
  },
);

HoverCard.displayName = 'HoverCard';
